import { useContext, useEffect, useState } from 'react';

import {
	FinancialFormTypeEnum,
	VehiclePurposeEnum,
} from 'models/financial-form/enums/financial-form.enums';
import { getLatestPreOffer } from 'models/financial-form/helpers/preOffer.helpers';
import { LoanOffer } from 'models/loan-offer/controllers/loan-offer.controller';
import { getSkus } from 'models/sku/controllers/sku.controller';
import { Sku, SkuStatusEnum } from 'models/sku/interfaces/sku.interface';
import { WhitelistNameEnum } from 'models/whitelist/interfaces/whitelist.interface';
import { ozonerContext } from '../../OzonerContext';

interface offerValidationHook {
	validOffer: boolean;
	offerExists: boolean | undefined;
	expiredOffer: boolean | undefined;
	validDownPayment: boolean | undefined;
	validTicket: boolean | undefined;
	validDuration: boolean | undefined;
	validVehiclePurpose: boolean | undefined;
	validProvider: boolean | undefined;
	validMaxPaymentAmount: boolean | undefined;
	validMaxFinancingAmount: boolean | undefined;
	validPreOffer: boolean | undefined;
	validScoring: boolean | undefined;
}

export function useValidOffer(): offerValidationHook {
	const { financialForm, vehicle } = useContext(ozonerContext);
	const [skus, setSkus] = useState<Sku[]>([]);
	const [validOffer, setValidOffer] = useState<boolean>(false);
	const [offerExists, setOfferExists] = useState<boolean>(false);
	const [expiredOffer, setExpiredOffer] = useState<boolean | undefined>(
		undefined,
	);
	const [validDownPayment, setValidDownPayment] = useState<boolean | undefined>(
		undefined,
	);
	const [validTicket, setValidTicket] = useState<boolean | undefined>(
		undefined,
	);
	const [validDuration, setValidDuration] = useState<boolean | undefined>(
		undefined,
	);
	const [validVehiclePurpose, setValidVehiclePurpose] = useState<
		boolean | undefined
	>(undefined);
	const [validProvider, setValidProvider] = useState<boolean | undefined>(
		undefined,
	);
	const [validMaxPaymentAmount, setValidMaxPaymentAmount] = useState<
		boolean | undefined
	>(undefined);
	const [validMaxFinancingAmount, setValidMaxFinancingAmount] = useState<
		boolean | undefined
	>(undefined);
	const [validPreOffer, setValidPreOffer] = useState<boolean | undefined>(
		undefined,
	);
	const [validScoring, setValidScoring] = useState<boolean | undefined>(
		undefined,
	);

	useEffect(() => {
		getSkus({
			status: SkuStatusEnum.ACTIVE,
			requirePricedVariant: true,
			limit: -1,
		})
			.then((result) => setSkus(result.data))
			.catch(() => {});
	}, []);

	const loanForm = financialForm.form as any;
	const isCreditLinePayjoy =
		financialForm.form.type === FinancialFormTypeEnum.creditLine;
	const isPayJoyEmployeesWhitelist =
		loanForm.whitelistName === WhitelistNameEnum.payJoyEmployees;

	const selectedSku = skus.find((s) => s._id === loanForm.sku);
	const selectedSkuVariant = selectedSku?.variants?.find(
		(v) => v._id === loanForm.skuVariant,
	);

	const loanDuration: number | undefined = isCreditLinePayjoy
		? loanForm.loanDuration
		: loanForm.loanApplication?.loanDuration;
	const maxPeriodicPayment: number | undefined = isCreditLinePayjoy
		? loanForm.maxPeriodicPayment
		: undefined;
	const maxFinancingAmount: number | undefined =
		isCreditLinePayjoy && isPayJoyEmployeesWhitelist
			? loanForm.maxFinancingAmount
			: undefined;
	const downPaymentAmount: number | undefined = isCreditLinePayjoy
		? loanForm.downPayment
		: loanForm.loanApplication?.downPayment;

	const offer = loanForm.offer as LoanOffer | undefined;

	useEffect(() => {
		const offerExistsValue = offer !== undefined;
		setOfferExists(offerExistsValue);

		const downPayment =
			downPaymentAmount !== undefined
				? downPaymentAmount === offer?.downPaymentAmount
				: undefined;
		setValidDownPayment(downPayment);

		const expectedTicketPrice =
			vehicle?.marketPrice ??
			selectedSkuVariant?.price ??
			selectedSku?.minPrice;
		const ticketPrice =
			expectedTicketPrice !== undefined
				? expectedTicketPrice === offer?.ticketPrice
				: undefined;
		setValidTicket(ticketPrice);

		const commercialUse =
			loanForm.vehiclePurpose === VehiclePurposeEnum.comercial;
		const vehiclePurpose =
			loanForm.vehiclePurpose !== undefined
				? offer?.commercialUse === commercialUse
				: undefined;
		setValidVehiclePurpose(vehiclePurpose);

		const providerSku = loanForm.provider ? true : undefined;
		setValidProvider(providerSku);

		const duration =
			loanDuration !== undefined ? loanDuration === offer?.duration : undefined;
		setValidDuration(duration);

		let baseValid =
			!!downPayment && !!ticketPrice && !!vehiclePurpose && !!duration;

		if (isCreditLinePayjoy) {
			const periodicPayment =
				maxPeriodicPayment !== undefined
					? offer?.periodicPayment !== undefined &&
					  offer?.periodicPayment <= maxPeriodicPayment
					: undefined;

			setValidMaxPaymentAmount(periodicPayment);
			baseValid = baseValid && !!periodicPayment;

			if (isPayJoyEmployeesWhitelist) {
				const financingAmount =
					maxFinancingAmount !== undefined
						? offer?.financingAmount !== undefined &&
						  offer?.financingAmount <= maxFinancingAmount
						: undefined;

				setValidMaxFinancingAmount(financingAmount);
				baseValid = baseValid && !!financingAmount;
			}
		} else {
			const latestPreOffer = getLatestPreOffer(
				loanForm.loanApplication?.preOffers,
			);
			const preOffer = latestPreOffer
				? latestPreOffer.status === 'approved'
				: undefined;
			setValidPreOffer(preOffer);

			const preOfferScoring =
				preOffer && offerExistsValue
					? latestPreOffer?.scoring.profileTier === offer.profileTier
					: undefined;
			setValidScoring(preOfferScoring);
			baseValid = baseValid && !!preOffer && !!preOfferScoring;
		}

		if (!offer) {
			setValidOffer(false);
			return;
		}

		const offerExpiredValue = offerExistsValue
			? !!offer.expirationDate && new Date(offer.expirationDate) < new Date()
			: undefined;

		setExpiredOffer(!!offerExpiredValue);
		setValidOffer(baseValid && !offerExpiredValue);
	}, [
		offer,
		vehicle,
		selectedSkuVariant,
		selectedSku,
		loanForm,
		downPaymentAmount,
		loanDuration,
		isCreditLinePayjoy,
		isPayJoyEmployeesWhitelist,
		maxFinancingAmount,
		maxPeriodicPayment,
	]);

	return {
		validOffer,
		expiredOffer,
		offerExists,
		validDownPayment,
		validTicket,
		validDuration,
		validVehiclePurpose,
		validProvider,
		validMaxPaymentAmount,
		validMaxFinancingAmount,
		validPreOffer,
		validScoring,
	};
}
